import {
  Path,
  sanitizeHrefSlashes,
  sanitizePathSlashes,
} from '@cora/root-app-common/routing';

interface Menu {
  iconUrl: string;
  apiUrl: string;
  deprecatedApiUrl: string;
}

interface AzureAd {
  host: string;
  clientId: string;
  tenant: string;
  authority: string;
  redirectUri: string;
}

interface AzureAdB2C {
  host: string;
  clientId: string;
  authority: string;
  redirectUri: string;
  postLogoutRedirectUri: string;
  knownAuthorities: string;
  mainWindowRedirectUri: string;
}

interface Keycloak {
  url: string;
  clientId: string;
  redirectUri: string;
  enableLogging: boolean;
  realm1: string;
  idp1?: string;
  host1: string;
  realm2?: string;
  idp2?: string;
  host2: string;
}

interface Intl {
  defaultLanguage: string;
}

interface NPS {
  baseApi: string;
  host: string;
}

interface DatadogRUM {
  enabled: boolean;
  applicationId: string;
  clientToken: string;
  site: string;
  sessionSampleRate: number;
  sessionReplaySampleRate: number;
  trackResources: boolean;
  trackLongTasks: boolean;
  trackUserInteractions: boolean;
}

interface IDPConfig {
  cdd: {
    dns: string;
    idp1: string;
    idp2: string;
  };
  revenda: {
    dns: string;
    idp1: string;
    idp2: string;
  };
}

interface DownloadCenter {
  baseApi: string;
  poolingIntervalSeconds: number;
  notificationPoolingIntervalSeconds: number;
}

interface FeatureToggle {
  enableKeycloak: boolean;
  showLanguageMenu: boolean;
  loginScreenEnabled: boolean;
  keycloakEnabledPathsCdd: string[];
  keycloakEnabledPathsRevenda: string[];
  donwloadCenterEnabledPaths: string[];
}

interface AvailableTenants {
  [hostname: string]: string[];
}

interface Tenant {
  availableTenants: AvailableTenants;
  tenantSelectionUrl: string;
}

interface RootAppConfig {
  sentryDsn: string;
  rootPath: Path;
  apiGatewayPrefix: string;
  environment: string;
  showEnvironmentLabel: boolean;
  release: string;
  i18n: Intl;
  nps: NPS;
  menu: Menu;
  idpConfig: IDPConfig;
  azureAd: AzureAd;
  azureAdB2C: AzureAdB2C;
  keycloak: Keycloak;
  apimSubscriptionKey: string;
  datadogRUM: DatadogRUM;
  downloadCenter: DownloadCenter;
  featureToggle: FeatureToggle;
  tenant: Tenant;
  userProfileApiUrl: string;
  mfeIndexUrl: string;
}

type Modify<T, R> = Omit<T, keyof R> & R;

type RawRootAppConfig = Modify<
  RootAppConfig,
  {
    rootPath: string;
    showEnvironmentLabel: string;
    menu: Menu;
    idpConfig: string;
    tenant: Modify<
      Tenant,
      {
        availableTenants: string;
      }
    >;
    keycloak: Omit<
      Modify<
        Keycloak,
        {
          enableLogging: string;
        }
      >,
      'redirectUri'
    >;
    datadogRUM: Modify<
      DatadogRUM,
      {
        enabled: string;
        sessionSampleRate: string;
        sessionReplaySampleRate: string;
        trackResources: string;
        trackLongTasks: string;
        trackUserInteractions: string;
      }
    >;
    downloadCenter: Modify<
      DownloadCenter,
      {
        poolingIntervalSeconds: string;
        notificationPoolingIntervalSeconds: string;
      }
    >;
    featureToggle: Modify<
      FeatureToggle,
      {
        enableKeycloak: string;
        showLanguageMenu: string;
        loginScreenEnabled: string;
        keycloakEnabledPathsCdd: string;
        keycloakEnabledPathsRevenda: string;
        donwloadCenterEnabledPaths: string;
      }
    >;
  }
>;

const toBoolean = (value: string) => String(value).toLowerCase() === 'true';

const toObject = <T>(value: string): T | null => {
  try {
    return JSON.parse(value);
  } catch {
    console.log('Erro ao parsear objeto de configuração', value);
    return null;
  }
};

const parseIdpConfig = (value: string): IDPConfig => {
  const defaultConfig: IDPConfig = {
    cdd: { dns: '', idp1: '', idp2: '' },
    revenda: { dns: '', idp1: '', idp2: '' },
  };

  const preParsed = toObject<IDPConfig>(value) ?? defaultConfig;

  const populate = (input: IDPConfig['cdd']) => {
    return {
      dns: input.dns || '',
      idp1: input.idp1 || '',
      idp2: input.idp2 || '',
    };
  };

  const revenda = populate(preParsed.revenda);
  const cdd = populate(preParsed.cdd);

  return {
    revenda,
    cdd,
  };
};

const parseDonwloadCenterPaths = (value: string) => {
  let result: string[] = [];
  try {
    const parsed = JSON.parse(value) as string[];
    if (Array.isArray(parsed)) {
      result = parsed;
    }
  } catch (err) {
    console.warn('Parse download center paths error', err);
  }

  return result;
};

const tryParseArray = (entry: string) => {
  const parsed = JSON.parse(entry);
  if (Array.isArray(parsed)) {
    return parsed;
  }

  return [];
};

const parseTenant = (rawTenant: RawRootAppConfig['tenant']): Tenant => {
  const availableTenants =
    toObject<AvailableTenants>(rawTenant.availableTenants) ?? {};

  return {
    availableTenants,
    tenantSelectionUrl: rawTenant.tenantSelectionUrl,
  };
};

const parseConfig = (rawConfig: RawRootAppConfig): RootAppConfig => ({
  ...rawConfig,
  azureAd: {
    ...rawConfig.azureAd,
    redirectUri: sanitizeHrefSlashes(`${window.location.origin}/`),
  },
  idpConfig: parseIdpConfig(rawConfig.idpConfig),
  tenant: parseTenant(rawConfig.tenant),
  azureAdB2C: {
    ...rawConfig.azureAdB2C,
  },
  keycloak: {
    ...rawConfig.keycloak,
    url: sanitizeHrefSlashes(rawConfig.keycloak.url),
    redirectUri: window.location.origin,
    enableLogging: toBoolean(rawConfig.keycloak.enableLogging),
  },
  rootPath: sanitizePathSlashes(rawConfig.rootPath),
  showEnvironmentLabel: toBoolean(rawConfig.showEnvironmentLabel),
  menu: { ...rawConfig.menu },
  sentryDsn:
    rawConfig.sentryDsn !== null &&
    rawConfig.sentryDsn !== 'SENTRY_FRONTEND_DSN' &&
    rawConfig.sentryDsn !== 'undefined'
      ? rawConfig.sentryDsn
      : '',
  datadogRUM: {
    ...rawConfig.datadogRUM,
    enabled: toBoolean(rawConfig.datadogRUM.enabled),
    sessionSampleRate: parseInt(rawConfig.datadogRUM.sessionSampleRate),
    sessionReplaySampleRate: parseInt(
      rawConfig.datadogRUM.sessionReplaySampleRate,
    ),
    trackResources: toBoolean(rawConfig.datadogRUM.trackResources),
    trackLongTasks: toBoolean(rawConfig.datadogRUM.trackLongTasks),
    trackUserInteractions: toBoolean(
      rawConfig.datadogRUM.trackUserInteractions,
    ),
  },
  downloadCenter: {
    ...rawConfig.downloadCenter,
    poolingIntervalSeconds: parseInt(
      rawConfig.downloadCenter.poolingIntervalSeconds,
    ),
    notificationPoolingIntervalSeconds: parseInt(
      rawConfig.downloadCenter.notificationPoolingIntervalSeconds,
    ),
  },
  featureToggle: {
    ...rawConfig.featureToggle,
    showLanguageMenu: toBoolean(rawConfig.featureToggle.showLanguageMenu),
    enableKeycloak: toBoolean(rawConfig.featureToggle.enableKeycloak),
    loginScreenEnabled: toBoolean(rawConfig.featureToggle.loginScreenEnabled),
    keycloakEnabledPathsCdd: tryParseArray(
      rawConfig.featureToggle.keycloakEnabledPathsCdd,
    ),
    keycloakEnabledPathsRevenda: tryParseArray(
      rawConfig.featureToggle.keycloakEnabledPathsRevenda,
    ),
    donwloadCenterEnabledPaths: parseDonwloadCenterPaths(
      rawConfig.featureToggle.donwloadCenterEnabledPaths,
    ),
  },
});

export type {
  RootAppConfig,
  RawRootAppConfig,
  Menu,
  FeatureToggle,
  Tenant,
  AvailableTenants,
};

export { parseConfig };
